import { useDispatch, useSelector } from 'react-redux';
import { selectLang } from '../../store/ui/ui.selector';
import {
  Button,
  CheckboxButton,
  Dropdown,
  Input,
  Subtitle,
  Title,
  TrashIcon,
} from '../shared';
import { translate } from '../../lib/lang.helper';
import { useMemo, useState } from 'react';
import { Modal, Table, Button as AntBtn, Tag } from 'antd';
import { selectCropSpecies } from '../../store/utils/utils.selector';
import { selectAccount, selectIsAdmin } from '../../store/auth/auth.selector';
import { sendSingleRequest } from '../../apis';
import { updateAccountSetting } from '../../store/auth/auth.actions';
import { loadCropSpecies } from '../../store/utils/utils.actions';
import { showFeedback } from '../../store/ui/ui.actions';
import ModalFeedbackView from '../shared/feedback/ModalFeedbackView';
import { hashColor } from '../../entities/util-functions';
import { displayValueUnit, numToStr } from '../../lib/common.helpers';

interface IRow {
  id: string | number;
  name: string;
  probability?: number | null;
  size_min?: number | null;
  size_max?: number | null;
  price?: number | null;
  subtype?: string | null;
}

type TUKey =
  | 'name'
  | 'probability'
  | 'size_min'
  | 'size_max'
  | 'price'
  | 'subtype';

const columns = (lang: any, withSubtype: boolean, unitType: any): any => {
  let result = [
    {
      title: translate(lang, 'Name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <div data-name='name'>{name}</div>,
    },
    {
      title: translate(lang, 'Probability'),
      key: 'probability',
      render: (x: IRow) => (
        <div data-name='num_val'>
          {x.probability !== null && x.probability !== undefined
            ? `${x.probability} %`
            : '-'}
        </div>
      ),
    },
    {
      title: translate(lang, 'Min'),
      key: 'min',
      render: (x: IRow) => (
        <div>{displayValueUnit(x.size_min, unitType, 'size_mm', '-')}</div>
      ),
    },
    {
      title: translate(lang, 'Max'),
      key: 'max',
      render: (x: IRow) => (
        <div>{displayValueUnit(x.size_max, unitType, 'size_mm', '-')}</div>
      ),
    },
    {
      title: translate(lang, 'Price'),
      key: 'price',
      render: (x: IRow) => (
        <div>
          {x.price === undefined || x.price === null ? '-' : `$ ${x.price}`}
        </div>
      ),
      align: 'left',
    },
  ];
  if (withSubtype) {
    result = [
      {
        title: translate(lang, 'Subtype'),
        key: 'subtype',
        render: (x: IRow) =>
          x.subtype ? (
            <Tag color={hashColor(x.subtype)}>{x.subtype}</Tag>
          ) : (
            <span>{'-'}</span>
          ),
      },
      ...result,
    ];
  }
  return result;
};

const OysterHarvestTypes = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const isAdmin = useSelector(selectIsAdmin);
  const oysterFields = useSelector(selectCropSpecies);
  const accountSetting = useSelector(selectAccount);
  const onGrowWaste = accountSetting?.oyster_crops;
  const disableAutoFill = accountSetting?.auto_fill_harvest_species_disabled;
  const unitType = accountSetting?.display_unit;

  const tableData = useMemo(
    () => [
      {
        id: 'grown',
        name: translate(lang, 'Grown ons'),
        probability: onGrowWaste?.grow_ons,
        size_min: onGrowWaste?.grow_ons_json?.size_min,
        size_max: onGrowWaste?.grow_ons_json?.size_max,
        price: onGrowWaste?.grow_ons_json?.price,
      },
      {
        id: 'waste',
        name: translate(lang, 'Waste'),
        probability: onGrowWaste?.waste,
        size_min: onGrowWaste?.waste_json?.size_min,
        size_max: onGrowWaste?.waste_json?.size_max,
        price: onGrowWaste?.waste_json?.price,
      },
      ...oysterFields
        .sort((a, b) => a.subtype?.localeCompare(b.subtype ?? '') ?? -1)
        .map(x => ({
          id: x.id,
          name: x.name,
          probability: x.probability,
          price: x.price,
          size_min: x.size_min,
          size_max: x.size_max,
          subtype: x.subtype,
        })),
    ],
    [onGrowWaste, oysterFields],
  );

  const [subtypes, setSubtypes] = useState<string[]>(
    oysterFields
      .filter(x => !!x.subtype)
      .map(x => x.subtype!)
      .filter((v, i, a) => a.indexOf(v) === i),
  );
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState<IRow[]>(tableData);
  const [disabled, setDisabled] = useState(false);

  const showError = (message: string) =>
    dispatch(
      showFeedback({
        type: 'error',
        message,
        isMessageModal: true,
      }),
    );
  const updateField = (id: number | string, key: TUKey, value: string) => {
    const val =
      key === 'name' || key === 'subtype'
        ? value
        : value.length <= 0
          ? null
          : Number(value);
    const tmp = data.map(x => (x.id === id ? { ...x, [key]: val } : x));
    setData(tmp);
  };
  const addNewField = () => {
    setData([...data, { id: Date.now(), name: '' }]);
  };
  const removeField = (id: string | number) => {
    const tmp = data.filter(x => x.id !== id);
    setData(tmp);
  };
  const addNewSubtype = () => {
    const newSubtype = window.prompt(
      translate(lang, '_enter_new_subtype_name'),
    );
    if (
      newSubtype &&
      newSubtype.trim().length > 0 &&
      !subtypes.includes(newSubtype)
    ) {
      setSubtypes([...subtypes, newSubtype]);
    }
  };
  const confirmClick = async () => {
    if (data.some(x => !x.name)) {
      showError(translate(lang, 'Please fill name'));
      return;
    }
    if (data.some(x => x.probability !== undefined && x.probability !== null)) {
      const sum = data.reduce((acc, x) => acc + (x.probability || 0), 0);
      if (Math.abs(100 - sum) > 0.0001) {
        showError(translate(lang, '_probability_values_total_100'));
        return;
      }
    }
    setDisabled(true);
    const deletedFields = oysterFields.filter(
      x => !data.some(y => y.id === x.id),
    );
    const species = data
      .filter(x => x.id !== 'grown' && x.id !== 'waste')
      .map(x => {
        const os = oysterFields.find(y => y.id === x.id);
        if (!os) {
          return { ...x, id: undefined };
        } else {
          return { ...os, ...x };
        }
      });
    const reqData = [
      ...species,
      ...deletedFields.map(x => ({ id: x.id, name: x.name, is_deleted: true })),
    ];
    const res = await sendSingleRequest(
      reqData,
      'POST',
      'api/account/crop_species',
      true,
    );
    if (!res.status) {
      showError(res.data?.message ?? 'Server error');
    }
    const oyster_crops = {
      ...onGrowWaste,
      grow_ons: data[0].probability,
      waste: data[1].probability,
      grow_ons_json: {
        size_min: data[0].size_min,
        size_max: data[0].size_max,
        price: data[0].price,
      },
      waste_json: {
        size_min: data[1].size_min,
        size_max: data[1].size_max,
        price: data[1].price,
      },
    };
    const r = await dispatch(updateAccountSetting({ oyster_crops }));
    if (r !== true) {
      showError(r ?? 'Server error');
    }
    await dispatch(loadCropSpecies());
    setDisabled(false);
    setVisible(false);
  };
  const probSum =
    Math.round(data.reduce((acc, x) => acc + (x.probability || 0), 0) * 100) /
    100;

  const tableColumns = [
    {
      title: translate(lang, 'Name'),
      key: 'name',
      render: (row: IRow) => (
        <Input
          label=''
          placeholder={translate(lang, 'Name')}
          type='text'
          value={row.name}
          onChange={e => updateField(row.id, 'name', e.target.value)}
          disabled={row.id === 'grown' || row.id === 'waste'}
        />
      ),
    },
    {
      title:
        translate(lang, 'Probability') +
        (Math.abs(100 - probSum) > 0.0001 ? ` (${probSum}%)` : ''),
      key: 'probability',
      render: (row: IRow) => (
        <Input
          label=''
          placeholder={translate(lang, 'Probability')}
          type='number'
          value={numToStr(row.probability)}
          onChange={e => updateField(row.id, 'probability', e.target.value)}
          unit='%'
          disabled={!isAdmin}
        />
      ),
    },
    {
      title: translate(lang, 'Min'),
      key: 'min',
      render: (row: IRow) => (
        <Input
          label=''
          type='number'
          value={numToStr(row.size_min)}
          onChange={e => updateField(row.id, 'size_min', e.target.value)}
          unit='mm'
          disabled={!isAdmin}
        />
      ),
    },
    {
      title: translate(lang, 'Max'),
      key: 'max',
      render: (row: IRow) => (
        <Input
          label=''
          type='number'
          value={numToStr(row.size_max)}
          onChange={e => updateField(row.id, 'size_max', e.target.value)}
          unit='mm'
          disabled={!isAdmin}
        />
      ),
    },
    {
      title: translate(lang, 'Price'),
      key: 'price',
      render: (row: IRow) => (
        <Input
          label=''
          type='number'
          value={numToStr(row.price)}
          onChange={e => updateField(row.id, 'price', e.target.value)}
          unit='$'
          disabled={!isAdmin}
        />
      ),
    },
    {
      title: '',
      key: 'action',
      render: (row: IRow) =>
        row.id !== 'grown' && row.id !== 'waste' ? (
          <Button
            width='full'
            size={0}
            type='bordered'
            color='red'
            className='d-flex justify-center'
            disabled={!isAdmin}
            onClick={() => removeField(row.id)}
          >
            <TrashIcon color='#cc0101' />
          </Button>
        ) : (
          <></>
        ),
    },
  ];
  const extendColumns = [
    {
      title: translate(lang, 'Subtype'),
      key: 'subtype',
      render: (row: IRow) =>
        row.id === 'grown' || row.id === 'waste' ? (
          <div>{'-'}</div>
        ) : (
          <Dropdown
            value={row.subtype ?? undefined}
            options={subtypes.map(x => ({ id: x, value: x, label: x }))}
            onChange={value => updateField(row.id, 'subtype', value)}
            placeholder={translate(lang, 'Select subtype')}
          />
        ),
    },
    ...tableColumns,
  ];

  const toggleDisableAutoFill = async (c: boolean) => {
    setDisabled(true);
    const res = await dispatch(
      updateAccountSetting({ auto_fill_harvest_species_disabled: c }),
    );
    setDisabled(false);
    if (res !== true) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(lang, res?.data?.message ?? 'Server error'),
        }),
      );
    }
  };

  return (
    <div className='mt-64 harvest-type-list mb-32'>
      <div className='d-flex align-center justify-between'>
        <Title size={6} color='black-3' align='default' fontWeight={500}>
          {translate(lang, '_oyster_harvest_type_list')}
        </Title>
      </div>
      <div className='d-flex white-card pt-12 pl-16 mb-8'>
        <div className='d-flex align-center'>
          <Button
            color='blue'
            size={3}
            width='small'
            type='fill'
            disabled={!isAdmin}
            onClick={() => {
              setVisible(true);
              setData(tableData);
            }}
          >
            {translate(lang, 'Update data')}
          </Button>
        </div>
      </div>
      <Table
        rowKey='id'
        className={'table table--units table--is__cursor'}
        pagination={false}
        columns={columns(lang, subtypes.length > 0, unitType)}
        dataSource={tableData}
      />
      {visible && (
        <Modal
          visible={true}
          onCancel={() => setVisible(false)}
          footer={null}
          width={1200}
        >
          <div className='wrap section-modal'>
            <div className='d-flex align-center mb-18'>
              <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
                {translate(lang, 'Add new')}
              </Subtitle>
            </div>
            <div className='section mb-7 pb-17'>
              <div className='d-flex align-center justify-between'>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    columnGap: '8px',
                  }}
                >
                  {subtypes.map(x => (
                    <div key={x}>
                      <Tag color='darkgreen'>{x}</Tag>
                    </div>
                  ))}
                </div>
                <AntBtn
                  type='primary'
                  size='small'
                  ghost={true}
                  disabled={!isAdmin}
                  onClick={addNewSubtype}
                >
                  {translate(lang, 'Add new subtype')}
                </AntBtn>
              </div>
            </div>
            <div className='section pb-17 mb-17'>
              {subtypes.length > 0 ? (
                <>
                  <div className='mb-7'>
                    <Table
                      rowKey='id'
                      className='table table--units table--small'
                      pagination={false}
                      columns={extendColumns}
                      dataSource={data.filter(x => !x.subtype)}
                    />
                    <AntBtn
                      size='small'
                      type='primary'
                      ghost={true}
                      disabled={!isAdmin}
                      onClick={addNewField}
                    >
                      {translate(lang, 'Add new')}
                    </AntBtn>
                  </div>
                  {subtypes
                    .filter(x => data.some(y => y.subtype === x))
                    .map((subtype, i) => (
                      <div key={subtype} className='mb-7'>
                        <Subtitle
                          color='black-1'
                          align='left'
                          size={3}
                          fontWeight={600}
                        >
                          {translate(lang, 'Subtype')}: {subtype}
                        </Subtitle>
                        <Table
                          rowKey='id'
                          className='table table--units table--small'
                          pagination={false}
                          columns={extendColumns}
                          dataSource={data.filter(x => x.subtype === subtype)}
                        />
                      </div>
                    ))}
                </>
              ) : (
                <>
                  <Table
                    rowKey='id'
                    className='table table--units table--small'
                    pagination={false}
                    columns={tableColumns}
                    dataSource={data}
                  />
                  <AntBtn
                    size='small'
                    type='primary'
                    ghost={true}
                    disabled={!isAdmin}
                    onClick={addNewField}
                  >
                    {translate(lang, 'Add new')}
                  </AntBtn>
                </>
              )}
            </div>
            <div className='modal-button d-flex justify-end'>
              <Button
                width={'small'}
                size={2}
                type='fill'
                color='green'
                className='rsp-btn ml-16'
                onClick={confirmClick}
                disabled={disabled || !isAdmin}
              >
                {translate(lang, 'Confirm')}
              </Button>
            </div>
          </div>
          <ModalFeedbackView />
        </Modal>
      )}
      <div className='mt-32'>
        <CheckboxButton
          label={translate(lang, '_disable_auto_fill_harvest')}
          disabled={disabled || !isAdmin}
          checked={disableAutoFill}
          onChange={e => toggleDisableAutoFill(e.target.checked)}
        />
      </div>
    </div>
  );
};

export default OysterHarvestTypes;
